import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/trpc/trpc";
import {
  CollectionDialog,
  type CollectionForEdit,
} from "@/components/collection-dialog";

export const Route = createFileRoute("/_authenticated/collections")({
  component: CollectionsPage,
});

function formatDateRange(c: CollectionForEdit) {
  const from = c.validFrom
    ? new Date(c.validFrom).toLocaleDateString("pl-PL")
    : null;
  const until = c.validUntil
    ? new Date(c.validUntil).toLocaleDateString("pl-PL")
    : null;
  if (!from && !until) return "Bezterminowo";
  if (from && until) return `${from} – ${until}`;
  if (from) return `od ${from}`;
  return `do ${until}`;
}

/** The fixed list under the rails. It always exists: until the salon saves
 *  it, the server answers with the defaults (every treatment). */
function InspirationsCard({ onEdit }: { onEdit: () => void }) {
  const { data: inspirations } = trpc.collection.getInspirations.useQuery();
  if (!inspirations) return null;

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">{inspirations.title}</h2>
            <Badge variant="outline">Lista pod belkami</Badge>
            {!inspirations.isActive && (
              <Badge variant="secondary">Ukryta</Badge>
            )}
          </div>
          {inspirations.subtitle && (
            <p className="text-sm text-muted-foreground">
              {inspirations.subtitle}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {inspirations.treatments.length === 0
              ? "Wszystkie aktywne zabiegi salonu"
              : inspirations.treatments.map((t) => t.name).join(" · ")}
          </p>
          <p className="text-xs text-muted-foreground">
            Klienci ze starszą wersją aplikacji nadal widzą tu wszystkie
            zabiegi.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="mr-1 size-4" />
          Edytuj
        </Button>
      </div>
    </Card>
  );
}

function CollectionsPage() {
  const { data: collections, isLoading } = trpc.collection.list.useQuery();
  const { data: inspirations } = trpc.collection.getInspirations.useQuery();
  const utils = trpc.useUtils();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CollectionForEdit | null>(null);
  const [editingInspirations, setEditingInspirations] = useState(false);

  const toggleActive = trpc.collection.update.useMutation({
    onSuccess: () => utils.collection.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const deleteCollection = trpc.collection.delete.useMutation({
    onSuccess: () => {
      utils.collection.list.invalidate();
      toast.success("Kolekcja usunięta");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Kolekcje</h1>
          <p className="text-sm text-muted-foreground">
            {collections?.length ?? 0} belek · Sekcje wyświetlane na ekranie
            głównym aplikacji
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setEditingInspirations(false);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-1 size-4" />
          Dodaj kolekcję
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Tytuł</th>
                <th className="px-4 py-3 font-medium">Zabiegi</th>
                <th className="px-4 py-3 font-medium">Kolejność</th>
                <th className="px-4 py-3 font-medium">Okres</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {collections?.map((c) => (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.title}</div>
                    {c.subtitle && (
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {c.subtitle}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {c.treatments.slice(0, 3).map((t) => (
                        <Badge
                          key={t.id}
                          variant="secondary"
                          className="text-xs"
                        >
                          {t.name}
                        </Badge>
                      ))}
                      {c.treatments.length > 3 && (
                        <span className="text-xs text-muted-foreground self-center">
                          +{c.treatments.length - 3}
                        </span>
                      )}
                      {c.treatments.length === 0 && (
                        <span className="text-xs text-muted-foreground">
                          brak
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.sortOrder}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDateRange(c as CollectionForEdit)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={c.isActive ? "default" : "secondary"}
                      className="cursor-pointer"
                      onClick={() =>
                        toggleActive.mutate({ id: c.id, isActive: !c.isActive })
                      }
                    >
                      {c.isActive ? "Aktywna" : "Nieaktywna"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(c as CollectionForEdit);
                          setEditingInspirations(false);
                          setDialogOpen(true);
                        }}
                      >
                        Edytuj
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm(`Usunąć kolekcję "${c.title}"?`)) {
                            deleteCollection.mutate({ id: c.id });
                          }
                        }}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {(collections?.length ?? 0) === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Brak kolekcji
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <InspirationsCard
        onEdit={() => {
          setEditing(null);
          setEditingInspirations(true);
          setDialogOpen(true);
        }}
      />

      <CollectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        collection={editing}
        inspirations={editingInspirations ? inspirations : null}
      />
    </div>
  );
}
